import { Navigate, useNavigate } from "react-router-dom";
import { ArrowRight, Palette, Sparkles } from "lucide-react";
import { Button } from "@eduviaos/ui-kit";
import { getCurrentTenant, type TenantVerticalLiteral } from "@eduviaos/shared";
import { useAuth } from "@/auth/AuthProvider";
import { useFirstRun } from "@/hooks/useFirstRun";
import { FirstRunShell } from "@/components/FirstRunShell";
import { buildFirstRunSteps, firstRunEyebrow } from "@/components/firstRunSteps";
import { BrandingPanel } from "@/components/branding/BrandingPanel";

/**
 * Phase F2b — IA first-run wizard step: Branding.
 *
 * The institution admin refines the logo + primary/secondary colours
 * seeded at tenant creation (SA picks a brand colour → this step lets
 * the IA replace it with their own mark + palette). Saving applies the
 * palette live to the admin shell (via `<TenantBrandingProvider>`) and
 * is served to the learners' portal through the public branding API —
 * one edit, both surfaces on-brand.
 *
 * Wizard flow: welcome → **branding** → invite-{staff|team} →
 * import-{students|learners} → dashboard. The step is vertical-agnostic
 * (schools want branding too); only the neighbouring step *labels*
 * de-school per vertical.
 */
export function FirstRunBrandingPage() {
  const navigate = useNavigate();
  const { firstRunComplete, status } = useAuth();
  const stateQuery = useFirstRun();

  const slug = getCurrentTenant().slug ?? null;

  // Wizard re-entry guard — mirrors the sibling steps.
  if (status === "authed" && firstRunComplete === true) {
    return <Navigate to="/dashboard" replace />;
  }

  const vertical = (stateQuery.data?.vertical ?? "school") as TenantVerticalLiteral;
  const isStudyCentre = vertical === "study_centre";
  const steps = buildFirstRunSteps(vertical);

  const onContinue = () => {
    navigate("/first-run/invite-staff");
  };

  return (
    <FirstRunShell
      steps={steps}
      activeStep="branding"
      eyebrow={firstRunEyebrow(vertical, "branding")}
    >
      <header className="motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-bottom-2 grid gap-3 motion-safe:duration-500">
        <span className="text-primary bg-primary/10 mx-auto inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium">
          <Palette className="size-3.5" aria-hidden="true" />
          Make it yours
        </span>
        <h1 className="text-center text-3xl font-semibold tracking-tight sm:text-4xl">
          Your brand, everywhere
        </h1>
        <p className="text-muted-foreground mx-auto max-w-xl text-balance text-center text-sm leading-relaxed sm:text-base">
          Upload your logo and pick your colours. We&apos;ll apply them across your admin portal
          {isStudyCentre ? " and your learners' portal" : " and your students' portal"} instantly.
          You can change these anytime from Settings → Branding.
        </p>
      </header>

      <div className="motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-bottom-3 mt-8 motion-safe:duration-700">
        {slug ? (
          <BrandingPanel slug={slug} embedded />
        ) : (
          <p className="text-muted-foreground text-center text-sm">
            Couldn&apos;t resolve your workspace. Continue and set branding later from Settings.
          </p>
        )}
      </div>

      <div className="motion-safe:animate-in motion-safe:fade-in mt-10 grid gap-3 motion-safe:duration-700">
        <Button
          size="lg"
          onClick={onContinue}
          className="group mx-auto w-full max-w-sm gap-2 transition-transform duration-150 ease-out active:scale-[0.97]"
        >
          Continue
          <ArrowRight
            className="size-4 transition-transform duration-200 ease-out group-hover:translate-x-0.5"
            aria-hidden="true"
          />
        </Button>
        <p className="text-muted-foreground mx-auto flex max-w-md items-center justify-center gap-1.5 text-center text-xs leading-relaxed">
          <Sparkles className="size-3 shrink-0" aria-hidden="true" />
          Save applies your palette live — skip if you&apos;d rather keep the default for now.
        </p>
      </div>
    </FirstRunShell>
  );
}
